import { Edit } from "@mui/icons-material";
import { Box, ClickAwayListener, IconButton, styled } from "@mui/material";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToggleWithText } from "components/toggleWithText";
import { CORE_WINDOW_IDS } from "contexts/windows";
import { renderActions, selectSubtrees, Subtree, SubtreeStatus } from "features/render";
import { useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";
import { selectActiveToolMode } from "slices/procoreUx";

import { TOOLBAR_CONTAINER, TOOLBAR_Z_INDEX } from "../constants";
import { bottomToolbarActions, selectActiveTab, selectIsRenderMenuOpen, selectShowUnavailableModes } from "./slice";

/**
 * Subtree rows shown in the floating render menu. `label` is an i18n key
 * (lowercase) that maps to existing translations in `locales/*\/translation.json`.
 * The order matches the desktop SubtreesPopover for cross-platform consistency.
 */
const subtreeRows: { subtree: Subtree; label: string }[] = [
    { subtree: "triangles", label: "mesh" },
    { subtree: "lines", label: "lines" },
    { subtree: "terrain", label: "terrain" },
    { subtree: "points", label: "pointCloud" },
];

const RENDER_BUTTON_SELECTOR = '[data-bottom-toolbar-button-id="render"]';
const BOTTOM_TOOLBAR_CONTAINER_SELECTOR = '[data-bottom-toolbar-container="true"]';

/**
 * Floating card that hovers above the bottom toolbar. Positions itself relative
 * to the toolbar container (which is `position: absolute`) so it stays anchored
 * to the bar across viewport changes.
 */
const MenuCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== "anchorLeft",
})<{ anchorLeft: number }>(({ anchorLeft }) => ({
    position: "absolute",
    bottom: "100%",
    left: `${anchorLeft}px`,
    marginBottom: "8px",
    background: TOOLBAR_CONTAINER.BG,
    border: TOOLBAR_CONTAINER.BORDER,
    borderRadius: `${TOOLBAR_CONTAINER.RADIUS}px`,
    boxShadow: TOOLBAR_CONTAINER.SHADOW,
    padding: "12px 16px",
    minWidth: "220px",
    zIndex: TOOLBAR_Z_INDEX.BOTTOM_TOOLBAR + 1,
    pointerEvents: "auto",
}));

const Row = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 0;
`;

const EditButton = styled(IconButton)`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
`;

/**
 * Mobile-styled floating Render modes menu.
 *
 * - Mounted as a sibling of `BottomToolbarTabs` inside `BottomToolbar.tsx`.
 * - Visibility is driven by `selectIsRenderMenuOpen` on the bottom-toolbar slice
 *   (independent from the desktop `SubtreesPopover` state).
 * - Auto-closes when the active toolbar tab changes or a tool is activated, and
 *   on outside clicks via `ClickAwayListener`.
 * - The pencil button on the Points Cloud row closes the menu and opens the
 *   Point Cloud Settings panel (drawer on phones, side panel on tablets+).
 */
export function BottomToolbarRenderMenu() {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(selectIsRenderMenuOpen);
    const subtrees = useAppSelector(selectSubtrees);
    const activeTab = useAppSelector(selectActiveTab);
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const showUnavailableModes = useAppSelector(selectShowUnavailableModes);
    const { togglePanel } = useUnifiedPanelToggle();
    const [anchorLeft, setAnchorLeft] = useState(0);

    // Auto-close on tab change / tool activation so the floating menu does not
    // linger over unrelated UI states. Dispatching closeRenderMenu while the
    // menu is already closed is a no-op, so the initial mount run is harmless.
    useEffect(() => {
        dispatch(bottomToolbarActions.closeRenderMenu());
    }, [activeTab, activeToolMode, dispatch]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const updateAnchor = () => {
            const renderButton = document.querySelector(RENDER_BUTTON_SELECTOR) as HTMLElement | null;
            const toolbarContainer = document.querySelector(BOTTOM_TOOLBAR_CONTAINER_SELECTOR) as HTMLElement | null;

            if (!renderButton || !toolbarContainer) {
                setAnchorLeft(0);
                return;
            }

            const buttonRect = renderButton.getBoundingClientRect();
            const containerRect = toolbarContainer.getBoundingClientRect();
            setAnchorLeft(Math.max(0, buttonRect.left - containerRect.left));
        };

        updateAnchor();
        window.addEventListener("resize", updateAnchor);

        return () => {
            window.removeEventListener("resize", updateAnchor);
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleSubtreeToggle = (subtree: Subtree) => {
        dispatch(renderActions.toggleSubtree({ subtree }));
    };

    const handleOpenPointCloudSettings = () => {
        dispatch(bottomToolbarActions.closeRenderMenu());
        togglePanel(CORE_WINDOW_IDS.RENDER_POINT_CLOUD);
    };

    const handleClickAway = () => {
        dispatch(bottomToolbarActions.closeRenderMenu());
    };

    // By default we match the desktop SubtreesPopover and hide subtree rows
    // that the current scene does not support. The dev/QA `showUnavailableModes`
    // slice flag (no in-app toggle) surfaces every row AND the Points Cloud
    // pencil entry so the settings drawer is reachable on a scene without
    // point cloud data.
    const visibleRows = showUnavailableModes
        ? subtreeRows
        : subtreeRows.filter(({ subtree }) => subtrees[subtree] !== SubtreeStatus.Unavailable);

    if (visibleRows.length === 0) {
        return null;
    }

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <MenuCard role="menu" aria-label="Render modes" anchorLeft={anchorLeft}>
                {visibleRows.map(({ subtree, label }) => {
                    const isUnavailable = subtrees[subtree] === SubtreeStatus.Unavailable;
                    const checked = subtrees[subtree] === SubtreeStatus.Shown;
                    const isPoints = subtree === "points";

                    return (
                        <Row key={subtree}>
                            <ToggleWithText
                                checked={checked}
                                onChange={() => handleSubtreeToggle(subtree)}
                                label={label}
                                disabled={isUnavailable}
                            />
                            {isPoints && showUnavailableModes ? (
                                <EditButton
                                    aria-label="Open point cloud settings"
                                    onClick={handleOpenPointCloudSettings}
                                    size="small"
                                >
                                    <Edit fontSize="small" />
                                </EditButton>
                            ) : null}
                        </Row>
                    );
                })}
            </MenuCard>
        </ClickAwayListener>
    );
}
